/**
 * Module: screens/creator/steps/_ProfileChips
 * Role: Shared chip-set used by Categories/Formats steps (FTR-1131 chrome
 *       rewrite). Underscore-prefixed = file-local helper, not exported from
 *       a public barrel.
 *
 *       Renders Russian `label` to the user but passes back the underlying
 *       enum `value` via `onToggle` — backend SSOT is preserved (see _labels.ts).
 *       CitiesStep uses its own chip variant because city values ARE the
 *       Russian strings (no enum mapping needed there).
 *
 * Used by: steps/{Categories,Formats}Step.tsx
 */

import { P, T } from '../../../styles/tokens';

export interface ChipOption {
  value: string;
  label: string;
}

export interface ChipsProps {
  items: ChipOption[];
  selected: string[];           // value[] (enum keys)
  /** When set, additional chips are visually blocked once `selected.length >= max`. */
  max?: number;
  onToggle(value: string): void;
}

export function Chips({ items, selected, max, onToggle }: ChipsProps) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      {items.map(({ value, label }) => {
        const active = selected.includes(value);
        const blocked = !active && max !== undefined && selected.length >= max;
        return (
          <button
            key={value}
            type="button"
            onClick={() => !blocked && onToggle(value)}
            style={{
              padding: '9px 15px',
              borderRadius: 20,
              border: `1.5px solid ${active ? P.terracotta : P.line}`,
              background: active ? P.terraSoft : P.card,
              color: blocked ? P.inkSoft : active ? P.terraDark : P.ink,
              font: T.subhead,
              fontWeight: active ? 600 : 400,
              cursor: blocked ? 'default' : 'pointer',
              opacity: blocked ? 0.45 : 1,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
